import { Injectable } from '@angular/core'
import { combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
    ConfigurationCategoryService,
    ConfigurationOptionCategory,
    ConfigurationOptionsCategoryMap,
} from './configurationCategory.service'
import { BasePathResolver } from '../../../../../framework/module-base-path/basePath.resolver'
import { ConfigurationStateService } from './configurationState.service'
import { stringify } from 'json-cycle'
import { NavigationCancel, NavigationEnd, Router } from '@angular/router'
import { Location } from '@angular/common'
import { AuthenticationService } from '../../../../../framework/authentication/services/authentication.service'
import { ModulePathService } from 'framework/module-path-service/module-path.service'

export type ConfigurationRoute = {
    path: string
    previousRoute: ConfigurationRoute | null
    nextRoute: ConfigurationRoute | null
    isAvailable: boolean
    isAccordionStep: boolean
    category?: ConfigurationOptionCategory
    index: number
    id: string
}

@UntilDestroy()
@Injectable()
export class ConfigurationRouteService {
    routeToAvailableRouteEnabled: boolean = true
    private path$: ReplaySubject<string> = new ReplaySubject()

    constructor(
        private configurationCategoryService: ConfigurationCategoryService,
        private configurationStateService: ConfigurationStateService,
        private authenticationService: AuthenticationService,
        private basePathResolver: BasePathResolver,
        private router: Router,
        private location: Location,
        private modulePathService: ModulePathService
    ) {
        merge(
            // trigger observable once
            of(1),
            // trigger when route changes
            this.router.events.pipe(
                untilDestroyed(this),
                filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel)
            )
        )
            .pipe(
                untilDestroyed(this),
                map(() => this.location.path().split('?')[0]),
                distinctUntilChanged()
            )
            .subscribe(path => {
                this.path$.next(path)
            })
    }

    getActiveConfigurationRoute(configurationId): Observable<ConfigurationRoute> {
        return combineLatest([this.getConfigurationRoutes(configurationId), this.path$]).pipe(
            map<[ConfigurationRoute[], string], ConfigurationRoute>(([routes, currentPath]) => {
                currentPath = this.modulePathService.getPathFromModuleRoot(currentPath)
                return routes.find(({ path }) => this.location.normalize(path) === currentPath)
            })
        )
    }

    /**
     * All routes and availability for given configurationId
     *
     * @param configurationId
     */
    getConfigurationRoutes(configurationId: string): Observable<ConfigurationRoute[]> {
        return combineLatest([
            // basepath of module
            this.basePathResolver.basePath$,

            // available categories in configuration
            this.configurationCategoryService.getConfigurationOptionCategories(configurationId),

            // all configuration options
            this.configurationCategoryService.getConfigurationOptions(configurationId),

            // configuration approval state
            this.configurationStateService.getHasBeenApproved(configurationId),

            // current loggedInState of user
            this.authenticationService.currentUser.pipe(map(() => this.authenticationService.isLoggedIn)),
        ]).pipe(
            untilDestroyed(this),
            debounceTime(10),
            map<
                [string, ConfigurationOptionCategory[], ConfigurationOptionsCategoryMap, boolean, boolean],
                ConfigurationRoute[]
            >(([basePath, categories, categoryMap, hasBeenApproved, isLoggedIn]) => {
                return [
                    // all option pages per category
                    ...categories
                        .filter(category => {
                            // Don't make a separate step for global options
                            if (category === ConfigurationOptionCategory.global) {
                                return false
                            }

                            // Don't make a car configuration step if the car options are all fixed
                            if (
                                category === ConfigurationOptionCategory.configurator &&
                                categoryMap[category].every(
                                    option =>
                                        option.__typename === 'SelectableDealOption' &&
                                        option.selectableValues.length === 1
                                )
                            ) {
                                return false
                            }

                            return true
                        })
                        .map((category, index) => ({
                            // first path should omit the category specific path
                            path: index === 0 ? basePath : `${basePath === '/' ? '' : basePath}/${category}`,
                            previousRoute: null,
                            nextRoute: null,
                            isAvailable:
                                !hasBeenApproved &&
                                ((index === 0 && ['configurator', 'contract'].indexOf(category) > -1) || isLoggedIn),
                            isAccordionStep: true,
                            category,
                            id: category,
                        })),

                    // confirm page
                    {
                        path: `${basePath === '/' ? '' : basePath}/confirm`,
                        previousRoute: null,
                        nextRoute: null,
                        isAvailable:
                            !hasBeenApproved &&
                            // user must be logged in
                            isLoggedIn,
                        // make sure all non optional values are set
                        // && Object.entries(categoryMap)
                        //   .filter(([category]) => category !== ConfigurationOptionCategory.global)
                        //   .every(([category, options]) => options
                        //     .filter(({optional}) => optional !== true)
                        //     .every(option => !!(option.value))
                        //   ),
                        isAccordionStep: true,
                        id: 'confirm',
                    },

                    // celebrate page
                    {
                        path: `${basePath === '/' ? '' : basePath}/celebrate`,
                        previousRoute: null,
                        nextRoute: null,
                        isAvailable: hasBeenApproved && isLoggedIn,
                        isAccordionStep: false,
                        id: 'celebrate',
                    },
                ].map((route, index, routes) =>
                    Object.assign(route, {
                        // add next and previous route references
                        index,
                        previousRoute: index === 0 ? null : routes[index - 1],
                        nextRoute: index === routes.length - 1 ? null : routes[index + 1],
                    })
                )
            }),
            distinctUntilChanged((a, b) => stringify(a) === stringify(b))
        )
    }

    findBestAvailableRoute(configurationId): Observable<ConfigurationRoute | null> {
        return combineLatest([
            this.configurationCategoryService.getConfigurationOptions(configurationId),
            this.getConfigurationRoutes(configurationId),
        ]).pipe(
            untilDestroyed(this),
            take(1),
            map<[ConfigurationOptionsCategoryMap, ConfigurationRoute[]], ConfigurationRoute | null>(
                ([configurationOptionsCategoryMap, routes]) =>
                    routes
                        .filter(route => route.isAvailable)

                        // get available routes
                        .find(
                            route =>
                                !route.hasOwnProperty('category') ||
                                !route.category ||
                                !configurationOptionsCategoryMap[route.category].every(option => !!option)
                        )
            )
        )
    }

    routeToAvailableRoute(configurationId) {
        if (this.routeToAvailableRouteEnabled) {
            this.findBestAvailableRoute(configurationId)
                .pipe(take(1))
                .subscribe(route => {
                    const path = route ? route.path : '/'
                    this.router.navigateByUrl(path)
                })
        }
    }
}
